import {isStockable} from "../stockable";
import React, {Fragment, useEffect, useMemo, useRef, useState} from "react";
import Router from "next/router";
import {ConfigProvider, Modal} from "antd";
import {plural, t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {ASSET_URL} from "../../../config";
import {applyLang} from "../../../i18n";
import {getLayoutDirection} from "../../../locales";
import ApiService from "../../../services/ApiService";
import CustomSection from "../components/custom-section";
import {getCampagneCurrencyOptions, getProductPrice, onCampagneDeviseChange, readCampagneDevise} from "../components/devise-choice";
import RenderConfetti from "../components/confetti";
import DonationPanel from "./DonationPanel";
import {Countdown, daysLeftText, GoalProgress} from "./Progress";
import {Donors, Teams} from "./Community";
import ThankYou from "./ThankYou";
import MetaV2 from "./MetaV2";
import usePreview from "./preview";
import Checkout from "./checkout";
import {mergeCheckoutDraft} from "./checkout/draft";
import {markCheckoutSuccessor, skipStaleCheckoutEntry} from "./checkout/history";
import {AmbassadeurSignup, AmbassadeurSpace} from "./Ambassadeur";
import LangSwitch from "./LangSwitch";
import {buildPalette, countryName, givingModel, goalOf, goalProgress, initials, isMonthlyDonation, lifecycle, sortedTiers, stockOf, timeLeft} from "./model";
import {orderedVisibleSections, showcaseChoice} from "./sections";
import {resolveDesignLayout} from "./layouts";
import styles from "./v2.module.css";

function plainText(value) {
    if (typeof value !== "string") return null
    const text = value.replace(/\*\*|__|[#>`]/g, "").trim()
    return text || null
}

/**
 * Logo de l'organisation, ou pastille à ses initiales comme sur la maquette s'il manque
 * ou ne se charge pas. L'échec peut survenir avant que React ne prenne la main (image
 * rendue côté serveur) : onError ne se déclenche alors jamais, d'où la vérification au
 * montage.
 */
function Logo({src, name}) {
    const ref = useRef(null)
    const [failed, setFailed] = useState(false)
    useEffect(() => {
        const img = ref.current
        if (img && img.complete && img.naturalWidth === 0) setFailed(true)
    }, [src])
    if (!src || failed) {
        return <span className={`${styles.logo} ${styles.logoInitials}`} aria-hidden="true">{initials(name || "")}</span>
    }
    return <img ref={ref} className={styles.logo} src={src} alt="" onError={() => setFailed(true)}/>
}

// Pluie de confettis de l'ancienne page (objectif atteint, ou « fête forcée »), sauf
// pour qui a demandé à limiter les animations.
function Celebration() {
    const [motion, setMotion] = useState(false)
    useEffect(() => {
        setMotion(!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches))
    }, [])
    return motion ? <RenderConfetti/> : null
}

function BannerCarousel({desktopItems, mobileItems, title}) {
    const [index, setIndex] = useState(0)
    const [isMobile, setIsMobile] = useState(false)
    const desktop = desktopItems.length ? desktopItems : mobileItems
    const mobile = mobileItems.length ? mobileItems : desktopItems
    useEffect(() => {
        const media = window.matchMedia?.("(max-width: 1023px)")
        if (!media) return
        const update = () => {setIsMobile(media.matches); setIndex(0)}
        update()
        if (media.addEventListener) media.addEventListener("change", update)
        else media.addListener?.(update)
        return () => {
            if (media.removeEventListener) media.removeEventListener("change", update)
            else media.removeListener?.(update)
        }
    }, [])
    useEffect(() => setIndex(0), [desktop.map(item => item.image).join("|"), mobile.map(item => item.image).join("|")])
    if (!desktop.length) return null
    const items = isMobile ? mobile : desktop
    const currentDesktop = desktop[index % desktop.length]
    const currentMobile = mobile[index % mobile.length]
    return <div className={styles.bannerCarousel}>
        <picture>
            <source media="(max-width: 1023px)" srcSet={ASSET_URL + currentMobile.image}/>
            <img className={styles.banner} src={ASSET_URL + currentDesktop.image} alt={title} fetchpriority="high"/>
        </picture>
        {items.length > 1 && <div className={styles.bannerControls}>
            <button type="button" aria-label={t`Précédent`} onClick={event => {event.stopPropagation(); setIndex(value => (value - 1 + items.length) % items.length)}}>‹</button>
            <span aria-live="polite">{index + 1} / {items.length}</span>
            <button type="button" aria-label={t`Suivant`} onClick={event => {event.stopPropagation(); setIndex(value => (value + 1) % items.length)}}>›</button>
        </div>}
    </div>
}

function ProductShowcase({context, campagneData, principalDevise, currency, formatMoney, palette, donationsOpen, onPick}) {
    const tiers = sortedTiers(campagneData?.products || []).map(product => applyLang({...product}, context.lang))
    if (!tiers.length) return null
    const giving = givingModel(campagneData?.config || {}, {stockable:isStockable(campagneData)})
    return <section className={styles.productsBlock} aria-labelledby="cv-products-title">
        <h2 id="cv-products-title"><Trans>Choisissez un montant</Trans></h2>
        <div className={styles.productsGrid}>
            {tiers.map((product, index) => {
                const soldOut = stockOf(campagneData, product.id) === 0
                const amount = getProductPrice(product, currency.devise, principalDevise, currency.type === "PRINCIPAL" ? 1 : currency.rate, currency.type)
                return <button
                    key={product.id}
                    type="button"
                    disabled={soldOut || !donationsOpen}
                    className={styles.tier}
                    style={{background: palette.tints[index] || palette.tints[palette.tints.length - 1]}}
                    onClick={() => onPick(product.id)}
                >
                    {product.image && <img src={ASSET_URL + product.image} alt="" className={styles.tierImage} loading="lazy"/>}
                    <span className={styles.tierPrice}>{formatMoney(amount, currency.devise)}{isMonthlyDonation(giving, product, showcaseChoice(giving, product)) && <span className={styles.tierPer}> <Trans>/mois</Trans></span>}</span>
                    {product.name && <span className={styles.tierLabel} dir="auto">{product.name}</span>}
                    {product.description && <span className={styles.tierDescription} dir="auto">{product.description}</span>}
                    {soldOut && <span className={styles.tierStock}><Trans>Épuisé</Trans></span>}
                </button>
            })}
        </div>
    </section>
}

function youtubeId(value) {
    if (!value || typeof value !== "string") return null
    const match = value.match(/(?:v=|youtu\.be\/|embed\/)([A-Za-z0-9_-]{6,})/)
    if (match) return match[1]
    return /^[A-Za-z0-9_-]{6,}$/.test(value.trim()) ? value.trim() : null
}

/**
 * Page de campagne v2 (design « Direction Verre »).
 * Mobile : bannière, où en est la campagne, compte à rebours, don ; puis le projet,
 * les équipes et les derniers dons. Bureau : le projet à gauche, le don toujours visible
 * à droite.
 */
export default function HomeV2({context, campagneData: servedCampagne, campagneTarget}) {
    // Aperçu depuis la fiche du back-office : la page applique les réglages en cours.
    const {campagneData, preview, selectBlock} = usePreview(servedCampagne, context)
    const config = campagneData?.config || {}
    const Layout = resolveDesignLayout(config.designVariant)
    const langValue = context.langValue || context.org?.language || "fr"
    const direction = getLayoutDirection(langValue)
    const principalDevise = (campagneData?.principalDevise || context.org?.devise || "eur").toUpperCase()
    const currencyOptions = getCampagneCurrencyOptions(context, campagneData)
    const currencyKey = JSON.stringify(currencyOptions.map(({devise, rate, type}) => [devise, rate, type]))
    const [showcaseDevise, setShowcaseDevise] = useState(currencyOptions[0].devise)
    useEffect(() => {
        const options = getCampagneCurrencyOptions(context, campagneData)
        const choose = (value) => setShowcaseDevise(options.some(option => option.devise === value) ? value : options[0].devise)
        choose(readCampagneDevise(campagneData?.id))
        return onCampagneDeviseChange(campagneData?.id, choose)
    }, [campagneData?.id, currencyKey]) // eslint-disable-line react-hooks/exhaustive-deps -- mise à jour quand les devises proposées changent
    const showcaseCurrency = currencyOptions.find(option => option.devise === showcaseDevise) || currencyOptions[0]

    const tiersCount = sortedTiers(campagneData?.products || []).length
    const palette = useMemo(() => buildPalette(config, Math.max(tiersCount, 2)), [JSON.stringify(config.tokens), config.primaryColor, config.ambiance, tiersCount])

    // En aperçu, un brouillon se regarde comme une page en ligne : c'est ce que verra
    // le donateur une fois la campagne publiée.
    const life = lifecycle(preview && (campagneData?.status === "DRAFT" || campagneData?.status === "ARCHIVED")
        ? {...campagneData, status: undefined, active: true, displayed: true}
        : campagneData)
    const goal = goalOf(campagneData)
    const progress = goalProgress(goal, campagneData?.stats?.total)
    // Le compteur est relu toutes les 5 secondes ; équipes et derniers dons ne sont
    // rechargés que si un don est réellement arrivé (ou a été affiché).
    const liveKey = progress.donations + ":" + progress.amount

    const formatMoney = (amount, devise = principalDevise) => {
        const value = Number(amount) || 0
        return new Intl.NumberFormat(langValue, {
            style: "currency",
            currency: String(devise || principalDevise).toUpperCase(),
            minimumFractionDigits: 0,
            maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
        }).format(value)
    }
    const formatNumber = (value, maximumFractionDigits = 0) =>
        new Intl.NumberFormat(langValue, {maximumFractionDigits}).format(Number(value) || 0)

    const sections = orderedVisibleSections(config.sections)
    const countdownAvailable = !!((life.notStarted && life.start) || (life.end && (!life.ended || !life.late)))
    const showProgress = config.hideBar !== true

    const title = plainText(config.title) || campagneData.label || ""
    const tagline = plainText(config.tagline) || plainText(config.metaDescription)

    const banners = Array.isArray(config.banners) ? config.banners.filter(b => b?.image) : []
    const desktopBanners = banners.filter(b => b.type !== "MOBILE")
    const mobileBanners = banners.filter(b => b.type === "MOBILE")
    const desktopBanner = desktopBanners[0] || mobileBanners[0]
    const video = youtubeId(config["banners.youtube"])
    const [videoOpen, setVideoOpen] = useState(false)

    const baseUrl = (context.org?.BASE_WEB_URL || "").replace(/\/+$/, "")
    // Lien personnel d'un ambassadeur : il ouvre son espace au-dessus de sa page d'équipe.
    const ambToken = typeof context.query?.amb === "string" && context.query.amb.length > 20 ? context.query.amb : null
    const teamParam = campagneTarget?.initial?.team || null
    // Équipe masquée (ambassadeur refusé ou en attente) : pas de page d'équipe.
    const teamPage = teamParam ? (campagneData.teams || []).find(item => item.param === teamParam && !item.hidden) : null

    // ---------- Page d'équipe : compteur propre à l'équipe ----------
    const [teamTotals, setTeamTotals] = useState(null)
    useEffect(() => {
        if (!teamPage) return
        let cancelled = false
        ApiService.get("/campagne/team/stats/" + campagneData.id + "/" + encodeURIComponent(teamPage.param)).then(res => {
            if (!cancelled && res?.data) setTeamTotals(res.data)
        }).catch(() => {})
        return () => { cancelled = true }
    }, [teamPage?.param, liveKey])
    const teamProgress = teamPage && teamTotals
        ? goalProgress({...goal, target: Number(teamPage.objectif) || 0, bonus: []}, teamTotals)
        : null

    // ---------- Écran de don de la campagne (réglage « v2Checkout ») ----------
    // Il remplace la page de paiement commune pour cette campagne, et seulement pour
    // elle : retirer le réglage rend aussitôt l'ancien parcours.
    const [checkout, setCheckout] = useState(null)
    const [checkoutValues, setCheckoutValues] = useState(null)
    const checkoutNavigationLocked = useRef(false)
    const staleCheckoutSkipInFlight = useRef(false)
    const lastHistoryState = useRef(null)
    const [requestedTier, setRequestedTier] = useState(null)
    const [promiseCompleted, setPromiseCompleted] = useState(false)
    useEffect(() => {
        setPromiseCompleted(false)
        setRequestedTier(null)
        setCheckout(null)
        setCheckoutValues(null)
        checkoutNavigationLocked.current = false
    }, [campagneData?.id])
    const checkoutInPage = config.v2Checkout === true && !preview

    // Après un retour depuis une autre page, ou un « Suivant » du navigateur, une
    // ancienne entrée paiement sans formulaire ne doit pas créer d'étape fantôme.
    useEffect(() => {
        const skip = () => {
            const current = window.history.state
            skipStaleCheckoutEntry(window.history, campagneData?.id, !!checkout, staleCheckoutSkipInFlight, lastHistoryState.current)
            lastHistoryState.current = current
        }
        skip()
        window.addEventListener("popstate", skip)
        return () => window.removeEventListener("popstate", skip)
    }, [campagneData?.id, checkout])

    useEffect(() => {
        if (!checkout) return
        // Le bouton « retour » du navigateur ramène au choix du montant.
        window.history.replaceState({...window.history.state, cvCheckout: null, cvCheckoutSkipped: null, cvCheckoutHasSuccessor: null, cvCheckoutBase: campagneData?.id}, "")
        window.history.pushState({...window.history.state, cvCheckout: campagneData?.id}, "")
        // Une navigation vers une autre page ajoute une entrée après le checkout.
        // Cette information permet de traverser l'étape périmée dans le bon sens
        // sans tenter d'avancer au-delà de la dernière entrée du navigateur.
        const markSuccessor = () => markCheckoutSuccessor(window.history, campagneData?.id)
        Router.events.on("routeChangeStart", markSuccessor)
        window.addEventListener("pagehide", markSuccessor)
        const onPop = () => {
            if (checkoutNavigationLocked.current) {
                window.history.pushState({...window.history.state, cvCheckout: campagneData?.id}, "")
                return
            }
            setCheckout(null)
            window.requestAnimationFrame(() => document.getElementById("cv-donate-title")?.focus({preventScroll: true}))
        }
        window.addEventListener("popstate", onPop)
        window.scrollTo({top: 0, behavior: "auto"})
        document.getElementById("cv-checkout-title")?.focus({preventScroll: true})
        return () => {
            window.removeEventListener("popstate", onPop)
            window.removeEventListener("pagehide", markSuccessor)
            Router.events.off("routeChangeStart", markSuccessor)
        }
    }, [checkout])

    const closeCheckout = () => {
        if (checkoutNavigationLocked.current) return
        if (window.history.state?.cvCheckout === campagneData?.id) window.history.back()
        else {
            setCheckout(null)
            window.requestAnimationFrame(() => document.getElementById("cv-donate-title")?.focus({preventScroll: true}))
        }
    }

    // ---------- Barre collante : cachée quand le bloc de don est visible ----------
    const donateRef = useRef(null)
    const [donateVisible, setDonateVisible] = useState(true)
    useEffect(() => {
        const target = donateRef.current
        if (!target || typeof IntersectionObserver === "undefined") return
        const observer = new IntersectionObserver(entries => {
            setDonateVisible(entries.some(entry => entry.isIntersecting))
        }, {rootMargin: "0px 0px -35% 0px"})
        observer.observe(target)
        return () => observer.disconnect()
    }, [])
    useEffect(() => {
        document.body.classList.toggle("campagne-v2-sticky", !donateVisible && context.render !== "embed")
        return () => document.body.classList.remove("campagne-v2-sticky")
    }, [donateVisible])

    const scrollToDonate = (focusHeading = false) => {
        const target = (checkout ? document.getElementById("cv-checkout-title") : document.getElementById("cv-donate")) || donateRef.current
        if (!target) return
        const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
        target.scrollIntoView({behavior: reduce ? "auto" : "smooth", block: "start"})
        // Une sélection dans la galerie peut changer de série ONCE → MONTHLY et
        // démonter les radios : le titre reste stable pendant cette mise à jour.
        const first = checkout ? target : (focusHeading
            ? target.querySelector("#cv-donate-title")
            : target.querySelector("button[role='radio']:not(:disabled), input, button"))
        first?.focus({preventScroll: true})
    }

    // ---------- Chiffres clés ----------
    const figures = []
    const unit = (n) => (n > 1 ? goal.unitLabel?.other : goal.unitLabel?.one) || ""
    if (goal.mode === "UNITS") {
        if (goal.target > 0) figures.push({value: `${formatNumber(goal.target, 1)} ${unit(goal.target)}`.trim(), label: t`à financer`})
        if (goal.unitPrice) figures.push({value: formatMoney(goal.unitPrice), label: goal.unitLabel?.one ? t`par ${goal.unitLabel.one}` : t`l'unité`})
    } else if (goal.mode === "COUNT") {
        if (goal.target > 0) figures.push({value: formatNumber(goal.target), label: t`dons attendus`})
    } else if (goal.target > 0) {
        figures.push({value: formatMoney(progress.target || goal.target), label: t`objectif`})
    }
    figures.push({value: formatNumber(progress.donations), label: plural(progress.donations, {one: "don reçu", other: "dons reçus"})})
    const left = life.end && !life.ended ? timeLeft(life.end) : null
    if (left) {
        figures.push(left.days >= 1
            ? {value: formatNumber(left.days), label: plural(left.days, {one: "jour restant", other: "jours restants"})}
            : {value: t`${left.hours} h`, label: t`restantes`})
    }
    if (goal.mode === "AMOUNT" && goal.target > 0 && figures.length < 4) {
        // Même cible et même pourcentage que la jauge : deux chiffres différents pour la
        // même campagne, sur le même écran, ne s'expliquent pas.
        figures.push({value: `${formatNumber(progress.percent)} %`, label: t`de l'objectif`})
    }

    const issuer = campagneData?.mainAssocInfo
    const logo = config.logo ? ASSET_URL + config.logo : context.org?.logo
    const hideOrg = config.hideOrg === true
    // Intégrée dans le site d'un client : son site porte déjà en-tête et pied de page,
    // et une barre collante resterait en bas d'une iframe agrandie à toute sa hauteur.
    const embed = context.render === "embed"
    const buttonText = config.donationButtonText || t`Je participe`

    // En aperçu, un clic sur un bloc ouvre son réglage dans la fiche.
    const blockProps = (block, baseClass = "", sectionId = null) => (preview
        ? {
            "data-cv-block": block,
            className: [baseClass, styles.previewBlock].filter(Boolean).join(" "),
            onClick: () => selectBlock(block, sectionId),
        }
        : (baseClass ? {className: baseClass} : {}))

    const cssVars = {
        "--cv-accent": palette.accent,
        "--cv-accent-deep": palette.accentDeep,
        "--cv-accent-light": palette.accentLight,
        "--cv-on-accent": palette.onAccent,
        "--cv-dark": palette.dark,
    }

    const progressBlock = showProgress ? (
        <GoalProgress
            goal={goal}
            progress={progress}
            matched={campagneData.matched}
            formatMoney={formatMoney}
            formatNumber={formatNumber}
        />
    ) : null

    const metaImage = config.metaImage || desktopBanner?.image || null

    return (
        <ConfigProvider direction={direction} theme={{token: {colorPrimary: palette.accent, fontFamily: "Figtree, system-ui, sans-serif"}}}>
            <div className={styles.root} dir={direction} lang={langValue} style={cssVars}>
                <MetaV2
                    seo={config}
                    context={context}
                    title={config.metaTitle || title}
                    description={config.metaDescription || tagline}
                    image={metaImage}
                    video={config.metaVideo}
                    canonical={baseUrl ? baseUrl + (teamPage ? "/" + teamPage.param : "") : null}
                    noindex={preview}
                />

                {/* Bulle WhatsApp de l'organisation, sur téléphone : masquée tant que le bloc de don
                    est à l'écran (elle couvrait le champ et le bouton), puis remontée au-dessus de
                    la barre collante. */}
                <style dangerouslySetInnerHTML={{__html: "@media (max-width: 1023px){body:not(.campagne-v2-sticky) .kehila-wa-fab{display:none;}body.campagne-v2-sticky .kehila-wa-fab{bottom:calc(96px + env(safe-area-inset-bottom, 0px));}}"}}/>

                <a href="#cv-donate" className={styles.skip} onClick={(e) => { e.preventDefault(); scrollToDonate() }}>
                    <Trans>Aller directement au don</Trans>
                </a>

                <Layout
                    blockProps={blockProps}
                    donateRef={donateRef}
                    header={!embed && <header className={styles.header}>
                        <div className={styles.brand}>
                            {!hideOrg && <Logo src={logo} name={context.org?.name}/>}
                            {!hideOrg && <span className={styles.orgName}>{context.org?.name}</span>}
                        </div>
                        <div className={styles.headerActions}>
                            <LangSwitch context={context}/>
                            {life.donationsOpen && (
                                <button type="button" className={styles.headerCta} onClick={() => scrollToDonate()}>{buttonText}</button>
                            )}
                        </div>
                    </header>}
                    ambassadorSpace={ambToken && (
                        <AmbassadeurSpace
                            context={context}
                            token={ambToken}
                            formatMoney={formatMoney}
                            formatNumber={formatNumber}
                        />
                    )}
                    teamBanner={teamPage && (
                        <div className={styles.teamBanner}>
                            <span><Trans>Vous soutenez l'équipe <strong>{teamPage.label}</strong></Trans></span>
                            <a href={baseUrl || "#"}><Trans>Voir toute la campagne</Trans></a>
                        </div>
                    )}
                    banner={<>
                        <BannerCarousel desktopItems={desktopBanners} mobileItems={mobileBanners} title={title}/>
                        {video && (
                            <button type="button" className={styles.videoButton} onClick={() => setVideoOpen(true)}>
                                <span aria-hidden="true">▶</span> <Trans>Voir la vidéo</Trans>
                            </button>
                        )}
                    </>}
                    donation={<>
                        {teamPage && teamProgress && (
                            <div style={{marginBottom: 18}}>
                                <GoalProgress
                                    goal={{...goal, bonus: []}}
                                    progress={teamProgress}
                                    matched={null}
                                    formatMoney={formatMoney}
                                    formatNumber={formatNumber}
                                />
                                <p className={styles.help}><Trans>Collecté par l'équipe {teamPage.label}</Trans></p>
                            </div>
                        )}
                        {!teamPage && <div {...blockProps("goal")}>{progressBlock}</div>}
                        <div {...blockProps("amounts")}>
                            {checkout && (
                                <Checkout
                                    context={context}
                                    campagneData={campagneData}
                                    donation={checkout}
                                    palette={palette}
                                    formatMoney={formatMoney}
                                    onBack={closeCheckout}
                                    draftValues={checkoutValues}
                                    onDraftChange={changes => setCheckoutValues(current => mergeCheckoutDraft(current, changes))}
                                    navigationLockedRef={checkoutNavigationLocked}
                                />
                            )}
                            {/* Le panneau reste monté pendant le paiement : le retour par le
                                bouton ou le navigateur retrouve montant, palier, fréquence,
                                quantité, équipe et devise exactement comme choisis. */}
                            <div style={{display: checkout ? "none" : undefined}} aria-hidden={!!checkout}>
                            <DonationPanel
                                key={campagneData?.id}
                                context={context}
                                campagneData={campagneData}
                                campagneTarget={campagneTarget}
                                life={life}
                                goal={goal}
                                palette={palette}
                                formatMoney={formatMoney}
                                preview={preview}
                                requestedTier={requestedTier}
                                onPromiseStateChange={setPromiseCompleted}
                                onCheckout={checkoutInPage ? (donation => setCheckout(donation)) : null}
                            />
                            </div>
                        </div>
                    </>}
                    story={<>
                        {/* `dir="auto"` : un texte resté en français sur une page en
                            hébreu garde son sens de lecture — sans lui, la ponctuation
                            finale sautait au début de la phrase. */}
                        <h1 className={styles.title} dir="auto">{title}</h1>
                        {tagline && <p className={styles.tagline} dir="auto">{tagline}</p>}
                        {teamPage?.description && <p className={styles.tagline} dir="auto" style={{marginTop: 12}}>{teamPage.description}</p>}

                        {figures.length > 0 && (
                            <ul {...blockProps("goal", styles.figures)}>
                                {figures.slice(0, 4).map((figure, index) => (
                                    <li key={index}>
                                        <div className={styles.figureValue} suppressHydrationWarning>{figure.value}</div>
                                        <div className={styles.figureLabel} suppressHydrationWarning>{figure.label}</div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </>}
                    sections={sections.map((section, index) => {
                            const key = section.id || `${section.type}-${index}`
                            switch (section.type) {
                                case "DATECOUNTER":
                                    return countdownAvailable ? <div key={key} {...blockProps("countdown", styles.countdownBlock)}><Countdown life={life} langValue={langValue}/></div> : null
                                case "PRODUCTS":
                                    return !checkout && !promiseCompleted && tiersCount ? <div key={key} {...blockProps("amounts", styles.contentBlock)}><ProductShowcase context={context} campagneData={campagneData} principalDevise={principalDevise} currency={showcaseCurrency} formatMoney={formatMoney} palette={palette} donationsOpen={life.donationsOpen} onPick={id => {setRequestedTier({id}); scrollToDonate(true)}}/></div> : null
                                case "TEAMPROGRESS":
                                    return teamPage ? null : <div key={key} {...blockProps("teams", styles.contentBlock)}><Teams context={context} campagneData={campagneData} liveKey={liveKey} formatMoney={formatMoney} formatNumber={formatNumber} baseUrl={baseUrl}/></div>
                                case "DONORS":
                                    return <div key={key} {...blockProps("donors", styles.contentBlock)}><Donors context={context} campagneData={campagneData} liveKey={liveKey} team={teamPage?.param || null} formatMoney={formatMoney}/></div>
                                case "SECTION":
                                    return <div key={key} className={`${styles.contentSection} ${preview ? styles.previewSection : ""}`}>
                                        <CustomSection id={section.id} section={section} context={context} campagneData={campagneData} campagneTarget={campagneTarget}/>
                                        {preview && <button
                                            type="button"
                                            className={styles.previewSectionAction}
                                            aria-label={`${t`Modifier`} : ${section.label || t`Modifier`}`}
                                            onClick={() => selectBlock("story", section.id)}
                                        ><span>{t`Modifier`}</span></button>}
                                    </div>
                                default:
                                    return null
                            }
                        })}
                    ambassadorSignup={!teamPage && life.donationsOpen && life.status === "LIVE" && (
                        <AmbassadeurSignup context={context} campagneData={campagneData} palette={palette} preview={preview}/>
                    )}
                    footer={!embed && <footer className={styles.footer}>
                        <div>
                            {issuer?.assocLabel && (
                                <Fragment>
                                    <Trans>Reçu fiscal émis par {issuer.assocLabel}</Trans>
                                    {issuer.charityNumber ? <Fragment>{" · "}<Trans>N° d'association</Trans>{" " + issuer.charityNumber}</Fragment> : ""}
                                    {issuer.country ? " · " + countryName(issuer.country, langValue) : ""}
                                    {" · "}
                                </Fragment>
                            )}
                            <Trans>Paiement sécurisé</Trans>
                        </div>
                        <div><Trans>Propulsé par Unisoft</Trans> · {new Date().getFullYear()}</div>
                    </footer>}
                    stickyBar={life.donationsOpen && !embed && (
                        <Fragment>
                            <div className={styles.stickySpacer} aria-hidden="true"/>
                            <div className={`${styles.sticky} ${donateVisible ? styles.stickyHidden : ""}`} aria-hidden={donateVisible}>
                                <div className={styles.stickyText}>
                                    <div className={styles.stickyValue}>
                                        {showProgress
                                            ? (goal.mode === "AMOUNT"
                                                ? `${formatMoney(progress.amount)} · ${formatNumber(progress.percent)} %`
                                                : `${formatNumber(progress.value, 1)} ${unit(progress.value)} · ${formatNumber(progress.percent)} %`)
                                            : title}
                                    </div>
                                    {daysLeftText(life) && <div className={styles.stickySub} suppressHydrationWarning>{daysLeftText(life)}</div>}
                                </div>
                                <button type="button" className={styles.stickyButton} tabIndex={donateVisible ? -1 : 0} onClick={() => scrollToDonate()}>
                                    {buttonText}
                                </button>
                            </div>
                        </Fragment>
                    )}
                />

                {video && (
                    <Modal open={videoOpen} onCancel={() => setVideoOpen(false)} footer={null} width={880} centered destroyOnHidden title={t`Vidéo de la campagne`}>
                        <div style={{position: "relative", paddingTop: "56.25%"}}>
                            <iframe
                                title={t`Vidéo de la campagne`}
                                src={"https://www.youtube-nocookie.com/embed/" + video + "?autoplay=1&rel=0"}
                                allow="autoplay; encrypted-media; picture-in-picture"
                                allowFullScreen
                                style={{position: "absolute", inset: 0, width: "100%", height: "100%", border: 0}}
                            />
                        </div>
                    </Modal>
                )}

                <ThankYou
                    context={context}
                    campagneData={campagneData}
                    title={title}
                    formatMoney={formatMoney}
                    progressSlot={showProgress ? progressBlock : null}
                />

                {!preview && (config.forceCelebration === true || (showProgress && progress.reached)) && <Celebration/>}
            </div>
        </ConfigProvider>
    )
}
